import { getErrorType, trackValidationError } from '../trackValidationError';
import type { FormSchema, FormSchemaField, FormSchemaOutput } from './types';

type FormField<T> = {
	value: T;
	blur: boolean;
	error: string;
	required: boolean;
};

type InferredValues<T extends FormSchema> = FormSchemaOutput<T>;

// zod 3 fields answer `isOptional()`, which is `safeParse(undefined).success`.
// zod mini fields have no such method, so they are asked the same question
// directly; both give the same answer for the same field.
const isFieldOptional = (field: FormSchemaField) =>
	'isOptional' in field ? field.isOptional() : field.safeParse(undefined).success;

// zod 3 (and a hand-written schema) expose the fields as `shape`. zod mini 3.25
// declares `shape` in its typing but only sets it on `_zod.def.shape`.
const getShape = (schema: FormSchema): FormSchema['shape'] =>
	schema.shape ?? schema._zod?.def.shape ?? {};

type FormValues<T extends FormSchema> = {
	[K in keyof InferredValues<T>]-?: Exclude<InferredValues<T>[K], undefined>;
};

type FormState<T extends FormSchema> = {
	[K in keyof FormValues<T>]: FormField<FormValues<T>[K]>;
};

function validateForm<T extends FormSchema>({
	baseSchema,
	schema,
	form,
	errorTypes
}: {
	baseSchema: T | undefined;
	schema: T;
	form: FormState<T>;
	errorTypes?: Partial<Record<keyof FormValues<T>, string>>;
}) {
	// Clear all errors first
	for (const key in getShape(baseSchema || schema)) {
		const typedKey = key as keyof FormValues<T>;
		form[typedKey].error = '';
		if (errorTypes) {
			delete errorTypes[typedKey];
		}
	}

	// Extract form values for validation
	const formValues = {} as Record<string, unknown>;
	for (const key in getShape(baseSchema || schema)) {
		const typedKey = key as keyof FormValues<T>;
		formValues[key] = form[typedKey].value;
	}

	// Validate entire form with schema
	const result = schema.safeParse(formValues);

	if (!result.success) {
		// Map errors back to form fields
		for (const issue of result.error.issues) {
			const fieldPath = issue.path[0] as keyof FormValues<T>;
			if (fieldPath && form[fieldPath]) {
				form[fieldPath].error = issue.message;
				// Kept out of the reactive form state: analytics-only, and writing
				// it there would change the shape every consumer destructures.
				if (errorTypes) {
					errorTypes[fieldPath] = getErrorType(issue);
				}
			}
		}
	}
}

const getInitialFormState = <T extends FormSchema>({
	baseSchema,
	schema,
	initialValues
}: {
	baseSchema: T | undefined;
	schema: T;
	initialValues?: FormValues<T>;
}) => {
	const shape = getShape(baseSchema || schema);
	const formState = {} as FormState<T>;
	const defaults = (initialValues ?? {}) as FormValues<T>;

	for (const key in shape) {
		const typedKey = key as keyof FormValues<T>;
		formState[typedKey] = {
			value: defaults[typedKey] ?? ('' as FormValues<T>[typeof typedKey]),
			blur: false,
			error: '',
			required: !isFieldOptional(shape[key])
		};
	}

	return formState;
};

export function createForm<T extends FormSchema>({
	baseSchema,
	schema,
	initialValues,
	trackingName,
	trackingStep,
	trackingIgnoreFields = []
}: {
	baseSchema?: T;
	schema: T;
	initialValues: FormValues<T>;
	/**
	 * Identifies the form in `FORM/VALIDATION_ERROR` analytics. Omit to opt the
	 * form out of validation tracking entirely.
	 */
	trackingName?: string;
	trackingStep?: () => string | number | undefined;
	/**
	 * Fields to leave out of validation tracking. Use for inputs whose real
	 * validity is owned by the component rather than the schema (e.g. PhoneInput,
	 * which stores a national number that the international-only schema rejects,
	 * and rewrites the value during autofill) — their schema error does not
	 * correspond to friction the user actually experienced.
	 */
	trackingIgnoreFields?: string[];
}) {
	const form = $state(getInitialFormState({ baseSchema, schema, initialValues }));

	// Analytics bookkeeping, deliberately non-reactive so reading it during
	// validation cannot re-trigger the effect that writes it.
	const errorTypes: Partial<Record<keyof FormValues<T>, string>> = {};
	let reportedErrors: Partial<Record<keyof FormValues<T>, string>> = {};

	const reportValidationErrors = () => {
		if (!trackingName) {
			return;
		}

		for (const key in getShape(baseSchema || schema)) {
			if (trackingIgnoreFields.includes(key)) {
				continue;
			}

			const typedKey = key as keyof FormValues<T>;
			// Only errors the user can actually see: an untouched field showing no
			// message has not caused any friction yet.
			const visibleError = form[typedKey].blur ? errorTypes[typedKey] : undefined;

			if (!visibleError) {
				// Clearing lets the same error report again if the user re-breaks the
				// field after fixing it — a second round trip is real friction.
				delete reportedErrors[typedKey];
				continue;
			}

			// Validation re-runs on every keystroke; without this the same unchanged
			// error would fire an event per character typed.
			if (reportedErrors[typedKey] === visibleError) {
				continue;
			}

			reportedErrors[typedKey] = visibleError;
			trackValidationError({
				field_name: key,
				error_type: visibleError,
				form: trackingName,
				step: trackingStep?.()
			});
		}
	};

	$effect(() => {
		validateForm({ baseSchema, schema, form, errorTypes });
		reportValidationErrors();
	});

	const resetForm = () => {
		const newState = getInitialFormState({ baseSchema, schema, initialValues });
		for (const key in getShape(baseSchema || schema)) {
			const typedKey = key as keyof FormValues<T>;
			form[typedKey].value = newState[typedKey].value;
			form[typedKey].blur = newState[typedKey].blur;
			form[typedKey].error = newState[typedKey].error;
		}
		reportedErrors = {};
	};

	const handleBlur = (field: keyof FormValues<T>) => {
		return () => {
			form[field].blur = true;
		};
	};

	const setBlur = (field: keyof FormValues<T>, blur: boolean) => {
		form[field].blur = blur;
	};

	const resetFormBlur = () => {
		for (const key in getShape(baseSchema || schema)) {
			const typedKey = key as keyof FormValues<T>;
			form[typedKey].blur = false;
		}
	};

	const getFormValid = (ignoreKeys: string[] = []) => {
		for (const key in getShape(baseSchema || schema)) {
			if (ignoreKeys.includes(key)) {
				continue;
			}

			const typedKey = key as keyof FormValues<T>;
			if (form[typedKey].error) {
				return false;
			}
		}
		return true;
	};

	const getFieldErrorMessage = (field: keyof FormValues<T>) => {
		return form[field].blur ? form[field].error : '';
	};

	return {
		form,
		handleBlur,
		setBlur,
		resetForm,
		resetFormBlur,
		getFormValid,
		getFieldErrorMessage
	};
}
